import { useCallback, useRef, useState, type ReactNode } from 'react'
import { CategoryPickerDialog } from '../categories/picker'
import type { Txn } from './transactionWire'
import { usePickedRowFocus } from './usePickedRowFocus'
import { useQuickCategory } from './useQuickCategory'

/**
 * One picker dialog per screen, opened from any row's category pill
 * (Transactions, Cashflow detail). The pick goes through `useQuickCategory`,
 * which PATCHes the row and invalidates both the transactions and cashflow
 * caches; nothing is removed locally — on the Uncategorised page the row
 * leaves when the refetch says so.
 *
 * **Focus.** Overlay restores to the pill that opened it; when the refetch
 * later removes that row, `usePickedRowFocus` moves focus to the content
 * region rather than leaving it on `<body>` (its docblock has the detail).
 */
export function useCategoryQuickPick(): { onCategoryClick: (t: Txn, opener: HTMLButtonElement) => void; dialog: ReactNode } {
  const [txn, setTxn] = useState<Txn | null>(null)
  const opener = useRef<HTMLElement | null>(null)
  const watch = usePickedRowFocus()
  const quick = useQuickCategory()

  const onCategoryClick = useCallback((t: Txn, el: HTMLButtonElement) => {
    opener.current = el
    setTxn(t)
  }, [])
  const dialog = (
    <CategoryPickerDialog
      open={txn !== null}
      onClose={() => setTxn(null)}
      value={txn?.categoryId ?? null}
      onPick={(id) => {
        if (txn === null) return
        watch(opener.current, quick.set(txn, id))
      }}
      context={txn === null ? undefined : { merchant: txn.merchant, date: txn.date, amountCents: txn.amountCents, currency: txn.currency }}
      allowNone
      restoreFocusTo={opener}
    />
  )
  return { onCategoryClick, dialog }
}
